import { useState, useEffect } from "react";
import { StaticImageData } from "next/image";
import { If } from "react-conditional-components-renderer";
import classNames from "classnames";

import {
  roundAndDropTrailingZeros,
  useWindowDimensions,
} from "@ascendnow/shared";
import { useChildrenArray } from "../../lib/helpers/hooks/useChildrenArray";
import AscDropdown from "../AscDropdown/AscDropdown";
import { AscDropdownOptionProps } from "../AscDropdown/AscDropdownOptions/AscDropdownOptions";
import AscLink from "../AscLink/AscLink";
import AscTag from "../AscTag/AscTag";
import AscUserDropdown from "../AscUserDropdown/AscUserDropdown";
import styles from "./AscSidebar.module.scss";
import AscSidebarItem, {
  AscSidebarItemProps,
} from "./AscSidebarItem/AscSidebarItem";

export interface AscSidebarProps {
  className?: string;
  children: JSX.Element[];
  collapsed?: boolean;
  width?: string;
  collapsedWidth?: string;
  header?: React.ReactNode;
  collapsedHeader?: React.ReactNode;
  headerClassName?: string;
  onClose?: () => void;
  onToggleCollapse?: () => void;
  selectedId?: string | null;
  onSelectionChange?: (activeItem: AscSidebarItemProps) => void;
  mobileOptions?: {
    name: string;
    avatarUrl: string | StaticImageData;
    globalRank?: number;
    classRank?: number;
    points?: number;
    coinsCount?: number;
    userDropdownOptions?: AscDropdownOptionProps[];
    globalRankUrl?: string;
    classRankUrl?: string;
    onLogout?: () => void;
  };
  collapsedHeaderClassName?: string;
}

function AscSidebar({
  className,
  children,
  collapsed = false,
  width = "15.25rem",
  collapsedWidth = "4.75rem",
  header,
  collapsedHeader,
  headerClassName,
  onClose,
  selectedId,
  onSelectionChange,
  mobileOptions,
  onToggleCollapse,
  collapsedHeaderClassName,
}: AscSidebarProps) {
  const sidebarItems = useChildrenArray<AscSidebarItemProps>(children);
  const [activeId, setActiveId] = useState<string | null>();
  const { width: windowSize } = useWindowDimensions();

  function handleActiveChange(activeItem: AscSidebarItemProps) {
    if (activeItem?.disabled) {
      return;
    }
    if (activeItem?.type === "function") {
      activeItem?.onClick?.();
    }

    setActiveId(activeItem?.id);

    onSelectionChange?.(activeItem);
  }

  function afterUserDropdownSelect() {
    onClose?.();
  }

  useEffect(() => {
    if (selectedId === null) {
      setActiveId(null);
      return;
    }

    if (selectedId === undefined && sidebarItems?.length) {
      handleActiveChange(sidebarItems[0]);
      return;
    }

    const itemToSelect = sidebarItems?.find(
      (sidebarItem) => sidebarItem.id === selectedId
    );

    if (!itemToSelect || itemToSelect?.id === activeId) {
      return;
    }

    handleActiveChange(itemToSelect);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    // Close sidebar on mobile on initial load and
    // when sidebar item is clicked (i.e. activeId changes)
    if (windowSize && windowSize < 768) {
      onClose?.();
    }
  }, [windowSize]);

  return (
    <aside
      data-testid="asc-sidebar"
      className={classNames(
        styles.ground,
        {
          [styles.collapsed]: collapsed,
        },
        className
      )}
      style={{ width: collapsed ? collapsedWidth : width }}
    >
      {collapsedHeader ? (
        <header
          className={classNames(
            styles.collapsedHeader,
            collapsedHeaderClassName
          )}
        >
          {collapsedHeader}
        </header>
      ) : null}
      {header ? (
        <header
          className={classNames(
            styles.header,
            {
              [styles.collapsedHeader]: !collapsedHeader && collapsed,
              collapsedHeaderClassName: !collapsedHeader && collapsed,
            },
            headerClassName
          )}
        >
          <div
            className={classNames(styles.desktop, {
              [styles.hide]: mobileOptions,
            })}
          >
            {header}
          </div>

          <div
            className={classNames(styles.mobile, {
              [styles.hide]: !mobileOptions,
            })}
          >
            <AscUserDropdown
              avatarUrl={
                typeof mobileOptions?.avatarUrl === "string"
                  ? mobileOptions?.avatarUrl
                  : mobileOptions?.avatarUrl?.src
              }
              name={mobileOptions?.name}
              globalRank={mobileOptions?.globalRank}
              classRank={mobileOptions?.classRank}
              points={mobileOptions?.points}
              globalRankUrl={mobileOptions?.globalRankUrl}
              classRankUrl={mobileOptions?.classRankUrl}
              onLogout={mobileOptions?.onLogout}
              innerContainerClassName={styles.userDropdown}
              portalContentClassName={styles.userDropdownPortalContent}
              afterSelect={afterUserDropdownSelect}
            >
              {mobileOptions?.userDropdownOptions?.map((option, index) => (
                <AscDropdown.Option key={index} {...option} />
              ))}
            </AscUserDropdown>
            <If test={!!mobileOptions?.coinsCount}>
              <AscTag
                type="coin"
                label={roundAndDropTrailingZeros(
                  mobileOptions?.coinsCount || 0
                )}
                className={styles.tag}
              />
            </If>
          </div>
        </header>
      ) : null}
      <ul className={styles.sideBarItems}>
        {sidebarItems.map((sidebarItem) => {
          return (
            <li
              key={sidebarItem?.id}
              onClick={() => {
                handleActiveChange(sidebarItem);
                if (windowSize < 768) {
                  onClose?.();
                }
              }}
              className={classNames(styles.sidebarItem, {
                [styles.disabled]: sidebarItem?.disabled,
                [styles.active]: activeId === sidebarItem?.id,
              })}
            >
              <AscLink
                className={styles.link}
                href={sidebarItem?.path || "#"}
                disabled={
                  sidebarItem?.type === "function" || sidebarItem?.disabled
                }
                innerWrapperTag={null}
              >
                {sidebarItem?.icon ? (
                  <span className={styles.icon}>{sidebarItem?.icon}</span>
                ) : null}
                <div className={styles.content}>{sidebarItem?.children}</div>
              </AscLink>
            </li>
          );
        })}
      </ul>
      <div className={styles.backdrop} onClick={onClose} />
      <div
        onClick={onToggleCollapse}
        className={styles.sidebarExpandCollapseArea}
      />
    </aside>
  );
}

AscSidebar.Item = AscSidebarItem;

export default AscSidebar;
